import * as React from 'react';
import type { Option } from './types';
import { Body2AccentTypography, Checkbox, Info, SimpleTooltip } from '@components/atoms';
import { shortify } from '@utils';

export type BasicCardItemProps<T> = {
	option: Option<T>;
	isLast?: boolean;
	onSelect: (option: Option<T>) => void;
	disabled?: boolean;
	isSelected?: boolean;
	indeterminate?: boolean;
} & React.RefAttributes<HTMLDivElement>;

export const BasicCardListItem = React.forwardRef(
	<T,>(
		{ option, disabled, isLast, onSelect, isSelected, indeterminate, ...rest }: BasicCardItemProps<T>,
		ref: React.Ref<HTMLDivElement>
	) => {
		const onClick = () => {
			if (!disabled) {
				onSelect(option);
			}
		};

		return (
			<div
				ref={isLast ? ref : null}
				className="flex w-full cursor-pointer items-start justify-center gap-[8px] pb-2 pl-0 pr-1 pt-2 hover:bg-borderSeparator active:bg-borderSeparator"
				{...rest}
			>
				<Checkbox
					data-aid="checkbox"
					disabled={disabled}
					checked={isSelected || Boolean(indeterminate)}
					onClick={onClick}
					indeterminate={indeterminate}
				/>
				{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
				<div onClick={onClick} className="flex w-full items-center justify-start gap-[4px]">
					<Body2AccentTypography className={disabled ? 'text-disabled' : 'text-primary'}>
						{shortify(option.label)}
					</Body2AccentTypography>
					{option.info && (
						<span className="flex items-center text-secondary">
							<SimpleTooltip content={option.info}>
								<Info />
							</SimpleTooltip>
						</span>
					)}
				</div>
			</div>
		);
	}
);

BasicCardListItem.displayName = 'BasicCardListItem';
